import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import {
  Container,
  Box,
  MenuItem,
  Typography,
  FormControl,
  InputLabel,
  Select,
  Paper,
  CircularProgress,
} from "@mui/material";
import { useThemeToggle } from "../theme"; // Import your theme hook
import UpdateDivesite from "../components/updatedivesiteform";
import auth from "../config/firebase";

// Styled components using your theme system
const PageContainer = styled(Container)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
}));

const ContentPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  backgroundColor: theme.palette.background.paper,
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  color: theme.palette.primary.main,
  fontWeight: theme.typography.fontWeightMedium,
}));

const FormContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(4),
}));

const StatusMessage = styled(Typography)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(4),
  backgroundColor:
    theme.palette.mode === "dark"
      ? theme.palette.background.paper
      : theme.palette.grey[50],
  borderRadius: theme.shape.borderRadius,
  marginTop: theme.spacing(2),
}));

const SiteUpdate = () => {
  const { theme, isMobile } = useThemeToggle();
  const [divesites, setDivesites] = useState([]);
  const [selectedDivesite, setSelectedDivesite] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDivesites = async () => {
      setLoading(true);
      try {
        const user = auth.currentUser;
        const token = user && (await user.getIdToken());
        const payloadHeader = {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        };
        const response = await fetch(
          `/api/divesites`,
          payloadHeader
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const json = await response.json();

        setDivesites(json);
        setError(null);
      } catch (e) {
        setError(e);
        setDivesites([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDivesites();
  }, []);

  const handleSelectChange = (e) => {
    const selectedId = e.target.value;
    setSelectedDivesite(selectedId);
  };

  if (loading) {
    return (
      <PageContainer maxWidth="lg">
        <ContentPaper elevation={2}>
          <StatusMessage variant="h6">
            <CircularProgress size={24} sx={{ mr: 2 }} />
            Loading dive sites...
          </StatusMessage>
        </ContentPaper>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer maxWidth="lg">
        <ContentPaper elevation={2}>
          <StatusMessage variant="h6" color="error">
            Error: {error.message}
          </StatusMessage>
        </ContentPaper>
      </PageContainer>
    );
  }

  return (
    <PageContainer maxWidth={isMobile ? "sm" : "lg"}>
      <ContentPaper elevation={2}>
        <PageTitle variant={isMobile ? "h5" : "h4"} component="h1">
          Update Dive Site
        </PageTitle>

        <FormContainer>
          <Typography
            variant="body1"
            component="p"
            sx={{ mb: 2, color: theme.palette.text.secondary }}
          >
            Select a dive site from the dropdown below to update its
            information.
          </Typography>

          <FormControl fullWidth variant="outlined">
            <InputLabel id="divesite-select-label">Dive Site</InputLabel>
            <Select
              labelId="divesite-select-label"
              id="divesite-select"
              value={selectedDivesite}
              label="Dive Site"
              onChange={handleSelectChange}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                    // Use theme values for styling MUI components that don't accept styled() wrapper
                    backgroundColor: theme.palette.background.paper,
                  },
                },
              }}
            >
              <MenuItem value="">
                <em>Select a dive site</em>
              </MenuItem>
              {divesites.map((divesite) => (
                <MenuItem key={divesite._id} value={divesite._id}>
                  {divesite.uddf.divesite.site.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FormContainer>

        {selectedDivesite && (
          <Box mt={4}>
            <Typography
              variant="h6"
              component="h2"
              sx={{ mb: 2, color: theme.palette.primary.main }}
            >
              Edit Site Details
            </Typography>
            <UpdateDivesite
              divesite={
                divesites.find((ds) => ds._id === selectedDivesite) || null
              }
            />
          </Box>
        )}
      </ContentPaper>
    </PageContainer>
  );
};

export default SiteUpdate;
